import { asset } from "@/lib/asset";
import { CATALOG, type CatalogEntry } from "./catalog.generated";

// Forwaro product catalog — driven by the confirmed inventory SSOT
// (產品素材盤點/catalog_master.json → src/mocks/catalog.generated.ts).
// Only the 135 listable SKUs (clean de-background image + material + dimensions)
// are surfaced; the 19 held SKUs are excluded at the data source until their
// data is completed. Regenerate the data file with:
//   python3 產品素材盤點/_raw/emit_app_catalog.py

export type Localized = { en: string; "zh-Hant": string; "zh-Hans"?: string };

export interface Product {
  id: string;
  name: Localized;
  category: string; // key in CATEGORY_LABEL
  material: Localized;
  style: Localized;
  images: string[];
  moq: number; // 0 means "on inquiry"
  leadTime: Localized;
  price: { min: number; max: number }; // 0,0 means "on request"
  certifications: string[];
  description: Localized;
  badge?: string;
  specs: {
    dimensions: Localized;
    weight: Localized;
    colors: Localized[];
    finish: Localized;
    assembly: Localized;
    packaging: Localized;
  };
  features: Localized[];
}

// Localized labels for the six product lines Forwaro produces for export.
// (No Beds — Forwaro's six B2B lines per the 2026 catalogue.)
export const CATEGORY_LABEL: Record<string, Localized> = {
  "Dining Tables": { en: "Dining Tables", "zh-Hant": "餐桌" },
  "Coffee Tables": { en: "Coffee Tables", "zh-Hant": "茶几 / 咖啡桌" },
  "Console Tables": { en: "Console Tables", "zh-Hant": "玄關桌 / 邊櫃" },
  Chairs: { en: "Chairs", "zh-Hant": "椅子" },
  Sofas: { en: "Sofas", "zh-Hant": "沙發" },
  "TV Cabinets": { en: "TV Cabinets", "zh-Hant": "電視櫃" },
};

const EMPTY: Localized = { en: "", "zh-Hant": "" };

// Map a generated catalog entry to the app Product shape. Material and
// dimensions come from the SSOT; pricing / MOQ / style / certifications are
// intentionally not surfaced in the B2B storefront (contact for a quotation).
const toProduct = (e: CatalogEntry): Product => ({
  id: e.id,
  name: { en: e.id, "zh-Hant": e.id }, // identity is the model number
  category: e.category,
  material: {
    en: e.material.en,
    "zh-Hant": e.material["zh-Hant"],
    "zh-Hans": e.material["zh-Hans"],
  },
  style: EMPTY,
  images: [asset(e.image)],
  moq: 0,
  leadTime: EMPTY,
  price: { min: 0, max: 0 },
  certifications: [],
  description: EMPTY,
  specs: {
    dimensions: { en: e.dimensions, "zh-Hant": e.dimensions },
    weight: EMPTY,
    colors: [],
    finish: EMPTY,
    assembly: EMPTY,
    packaging: EMPTY,
  },
  features: [],
});

export const products: Product[] = CATALOG.map(toProduct);

// Filter categories are derived from the SKUs actually present, so the
// products page never shows an empty product line.
export const categories = [
  { id: "all", label: "All Products" },
  ...Array.from(new Set(products.map((p) => p.category))).map((id) => ({ id, label: id })),
];

// Material / style facets are not surfaced as filters (see FilterSidebar).
export const materials: string[] = [];
export const styles: string[] = [];
